// src/utils/csvExport.ts
import { unparse } from "papaparse";
import { encode as iconvEncode } from "iconv-lite-umd";
import Term from "../../term/Term";

export type CsvEncoding = "utf-8" | "cp949" | "shift_jis";
export type CsvExportOptions = {
  filename?: string; // 기본: terms.csv
  includeBom?: boolean; // 기본: true (엑셀 호환)
  encoding?: CsvEncoding; // 기본: "utf-8"
  delimiter?: string; // 기본: ","
  newline?: "\r\n" | "\n"; // 기본: "\r\n" (엑셀 호환)
  quoteAll?: boolean; // 기본: false
};

/** Term[] -> CSV 문자열 생성 */
export function termsToCsvString(
  terms: Term[],
  opts: Pick<CsvExportOptions, "delimiter" | "newline" | "quoteAll"> = {}
): string {
  const { delimiter = ",", newline = "\r\n", quoteAll = false } = opts;

  // Swift에서 기대하는 헤더: T,D,P,E
  const fields: string[] = ["T", "D", "P", "E"];

  // Term -> CSV 레코드
  const data = terms.map((t) => ({
    T: (t.term ?? "").toString(),
    D: (t.definition ?? "").toString(),
    P: (t.pronunciation ?? "").toString(),
    E: (t.memo ?? "").toString(),
  }));

  return unparse(
    { fields, data },
    {
      delimiter,
      newline,
      quotes: quoteAll, // 모든 필드에 따옴표
      // quotes 배열 사용 시 필드별 설정 가능
      header: true,
      escapeFormulae: true, // =, +, -, @ 시작 문자열 보호
    }
  );
}

/** CSV 문자열을 파일로 저장 (브라우저 다운로드 트리거) */
export function saveCsvToDisk(
  csvString: string,
  {
    filename = "terms.csv",
    includeBom = true,
    encoding = "utf-8",
  }: Pick<CsvExportOptions, "filename" | "includeBom" | "encoding"> = {}
) {
  let bytes: Uint8Array;

  if (encoding === "utf-8") {
    const utf8 = new TextEncoder().encode(csvString);
    if (includeBom) {
      // UTF-8 BOM: 0xEF,0xBB,0xBF
      const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
      bytes = new Uint8Array(bom.length + utf8.length);
      bytes.set(bom, 0);
      bytes.set(utf8, bom.length);
    } else {
      bytes = utf8;
    }
  } else {
    // 단일바이트 인코딩(CP949/Shift_JIS)로 인코딩
    // iconv-lite-umd는 BOM을 붙이지 않으므로 includeBom 무시
    bytes = iconvEncode(csvString, encoding);
  }

  const blob = new Blob([bytes], {
    type: "text/csv;charset=" + encoding,
  });

  // a[download]로 저장 트리거
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** 한 방에: Term[] -> CSV -> 저장 */
export function exportTermsCsv(terms: Term[], options: CsvExportOptions = {}) {
  const { filename, includeBom, encoding, delimiter, newline, quoteAll } =
    options;
  const csv = termsToCsvString(terms, { delimiter, newline, quoteAll });
  saveCsvToDisk(csv, { filename, includeBom, encoding });
}
